import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
  InputAdornment, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  Google as GoogleIcon,
  Apple as AppleIcon,
  Mail as OutlookIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  Refresh as ResetIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { axiosService } from 'shared/helpers/axios';
import { apiErrorMessage } from 'shared/helpers/apiError';

/** Subscribe to My Calendar from Google, Apple or Outlook: a private .ics link the calendar app reads every
 *  few hours. The link is the key (calendar apps cannot sign in), so it can be reset. */
export default function CalendarSyncDialog({ open, onClose }) {
  const [feed, setFeed] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setConfirmReset(false);
    axiosService.get('/events/calendar-feed/')
      .then(({ data }) => setFeed(data))
      .catch((err) => setError(apiErrorMessage(err, 'Could not get your calendar link.')));
  }, [open]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(feed.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Copy did not work: select the link and copy it by hand.');
    }
  };

  const reset = async () => {
    setBusy(true);
    try {
      const { data } = await axiosService.post('/events/calendar-feed/');
      setFeed(data);
      setConfirmReset(false);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not make a new link.'));
    } finally {
      setBusy(false);
    }
  };

  const apps = feed ? [
    { label: 'Google Calendar', icon: <GoogleIcon />,
      href: `https://calendar.google.com/calendar/render?cid=${encodeURIComponent(feed.webcal_url)}` },
    { label: 'Apple Calendar', icon: <AppleIcon />, href: feed.webcal_url, sameTab: true },
    { label: 'Outlook.com', icon: <OutlookIcon />,
      href: `https://outlook.live.com/calendar/0/addfromweb?url=${encodeURIComponent(feed.url)}&name=Otable` },
    { label: 'Outlook (work or school)', icon: <OutlookIcon />,
      href: `https://outlook.office.com/calendar/0/addfromweb?url=${encodeURIComponent(feed.url)}&name=Otable` },
  ] : [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Sync with your calendar</DialogTitle>
      <DialogContent>
        {!feed && !error && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>}
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {feed && (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Your events, their deadlines and your meetings appear in your calendar app and stay up to date.
              Changes reach it within a few hours (Google can take up to a day).
            </Typography>

            <Stack spacing={1}>
              {apps.map((a) => (
                <Button key={a.label} variant="outlined" startIcon={a.icon} href={a.href}
                  target={a.sameTab ? undefined : '_blank'} rel="noopener noreferrer"
                  sx={{ justifyContent: 'flex-start', textTransform: 'none' }}>
                  Add to {a.label}
                </Button>
              ))}
            </Stack>

            <Box>
              <Typography variant="caption" color="text.secondary">Any other calendar: subscribe to this address</Typography>
              <TextField value={feed.url} size="small" fullWidth
                onFocus={(e) => e.target.select()}
                slotProps={{
                  input: {
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title={copied ? 'Copied' : 'Copy'}>
                          <IconButton size="small" onClick={copy} aria-label="Copy the link">
                            {copied ? <CheckIcon fontSize="small" color="success" /> : <CopyIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  },
                }} />
            </Box>

            <Alert severity="info" icon={false} sx={{ fontSize: '0.8rem' }}>
              Keep this link private: anyone who has it can see your calendar. The calendar app you add it
              to (Google, Apple, Microsoft) keeps a copy of your events' titles, times and places.
              {feed.last_fetched && ` A calendar app last read it ${formatDistanceToNow(new Date(feed.last_fetched), { addSuffix: true })}.`}
            </Alert>

            {confirmReset ? (
              <Alert severity="warning" action={(
                <Button color="inherit" size="small" onClick={reset} disabled={busy}>Make new link</Button>
              )}>
                The old link stops working; add the new one to your calendar apps again.
              </Alert>
            ) : (
              <Button size="small" color="inherit" startIcon={<ResetIcon />} onClick={() => setConfirmReset(true)}
                sx={{ alignSelf: 'flex-start', textTransform: 'none', color: 'text.secondary' }}>
                Shared it by mistake? Make a new link
              </Button>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Done</Button>
      </DialogActions>
    </Dialog>
  );
}
